// services/SupabaseService.js - Enhanced service with advanced features
import { supabase } from '@/lib/supabase'

export class SupabaseService {
  constructor(tableName) {
    this.tableName = tableName
  }

  // Basic CRUD operations
  async list(orderBy = 'created_at', limit = null, offset = 0) {
    let query = supabase
      .from(this.tableName)
      .select('*')
    
    if (orderBy.startsWith('-')) {
      query = query.order(orderBy.substring(1), { ascending: false })
    } else {
      query = query.order(orderBy, { ascending: true })
    }
    
    if (limit) {
      query = query.limit(limit)
    }

    if (offset > 0) {
      query = query.range(offset, offset + (limit || 1000) - 1)
    }
    
    const { data, error, count } = await query
    
    if (error) throw error
    return { data: data || [], count }
  }

  async create(data) {
    const { data: result, error } = await supabase
      .from(this.tableName)
      .insert(data)
      .select()
      .single()
    
    if (error) throw error
    return result
  }

  async createMany(dataArray) {
    const { data: result, error } = await supabase
      .from(this.tableName)
      .insert(dataArray)
      .select()
    
    if (error) throw error
    return result
  }

  async update(id, data) {
    const { data: result, error } = await supabase
      .from(this.tableName)
      .update(data)
      .eq('id', id)
      .select()
      .single()
    
    if (error) throw error
    return result
  }

  async updateMany(ids, data) {
    const { data: result, error } = await supabase
      .from(this.tableName)
      .update(data)
      .in('id', ids)
      .select()
    
    if (error) throw error
    return result
  }

  async delete(id) {
    const { error } = await supabase
      .from(this.tableName)
      .delete()
      .eq('id', id)
    
    if (error) throw error
    return true
  }

  async deleteMany(ids) {
    const { error } = await supabase
      .from(this.tableName)
      .delete()
      .in('id', ids)
    
    if (error) throw error
    return true
  }

  async getById(id) {
    const { data, error } = await supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single()
    
    if (error) throw error
    return data
  }

  // Advanced filtering and searching
  async filter(filters, orderBy = 'created_at', limit = null) {
    let query = supabase
      .from(this.tableName)
      .select('*')
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        query = query.eq(key, value)
      }
    })
    
    if (orderBy.startsWith('-')) {
      query = query.order(orderBy.substring(1), { ascending: false })
    } else {
      query = query.order(orderBy, { ascending: true })
    }
    
    if (limit) {
      query = query.limit(limit)
    }
    
    const { data, error } = await query
    
    if (error) throw error
    return data || []
  }

  async search(searchTerm, searchFields, orderBy = 'created_at', limit = null) {
    if (!searchTerm || !searchFields || searchFields.length === 0) {
      return this.list(orderBy, limit)
    }

    let query = supabase
      .from(this.tableName)
      .select('*')

    // Build OR condition for search across multiple fields
    const orConditions = searchFields
      .map(field => `${field}.ilike.%${searchTerm}%`)
      .join(',')
    
    query = query.or(orConditions)
    
    if (orderBy.startsWith('-')) {
      query = query.order(orderBy.substring(1), { ascending: false })
    } else {
      query = query.order(orderBy, { ascending: true })
    }
    
    if (limit) {
      query = query.limit(limit)
    }
    
    const { data, error } = await query
    
    if (error) throw error
    return data || []
  }

  // Real-time subscriptions
  subscribe(callback, eventType = '*') {
    const subscription = supabase
      .channel(`${this.tableName}-changes`)
      .on('postgres_changes', {
        event: eventType,
        schema: 'public',
        table: this.tableName
      }, callback)
      .subscribe()

    return subscription
  }

  // Count operations
  async count(filters = {}) {
    let query = supabase
      .from(this.tableName)
      .select('*', { count: 'exact', head: true })
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        query = query.eq(key, value)
      }
    })
    
    const { count, error } = await query
    
    if (error) throw error
    return count || 0
  }

  // Aggregation functions
  async aggregate(column, operation = 'sum', filters = {}) {
    let query = supabase
      .from(this.tableName)
      .select(`${column}.${operation}()`)
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        query = query.eq(key, value)
      }
    })
    
    const { data, error } = await query.single()
    
    if (error) throw error
    return data?.[operation] || 0
  }
}


// Extended service for specific entity needs
export class AutoService extends SupabaseService {
  constructor() {
    super('auto')
  }

  async getWithRelations(limit = null) {
    let query = supabase
      .from(this.tableName)
      .select(`
        *,
        clienti:cliente_id(nome, cognome, telefono, email),
        stati:stato_id(nome, descrizione),
        campate:campata_id(
          nome,
          sezioni:sezione_id(
            nome,
            tettoie:tettoia_id(nome)
          )
        )
      `)
      .order('created_at', { ascending: false })
    
    if (limit) {
      query = query.limit(limit)
    }
    
    const { data, error } = await query
    
    if (error) throw error
    return data || []
  }

  async updateCampataOccupancy(autoId, newCampataId, oldCampataId = null) {
    try {
      // Start a transaction-like operation
      if (oldCampataId) {
        await supabase
          .from('campate')
          .update({ occupata: false })
          .eq('id', oldCampataId)
      }

      if (newCampataId) {
        await supabase
          .from('campate')
          .update({ occupata: true })
          .eq('id', newCampataId)
      }

      // Update the auto record
      const { data, error } = await this.update(autoId, { campata_id: newCampataId })
      
      if (error) throw error
      return data
    } catch (error) {
      // Rollback on error would be ideal, but Supabase doesn't support transactions in the client
      console.error('Error updating campata occupancy:', error)
      throw error
    }
  }

  async getOccupancyStats() {
    const { data, error } = await supabase
      .from('campate')
      .select('occupata')
    
    if (error) throw error
    
    const total = data.length
    const occupied = data.filter(c => c.occupata).length
    
    return {
      total,
      occupied,
      available: total - occupied,
      occupancyRate: total > 0 ? (occupied / total) * 100 : 0
    }
  }


  // Add this method to your existing AutoService class in SupabaseService.js

  // NEW: Check if numero_telaio already exists
  async checkNumeroTelaioDuplicate(numeroTelaio, excludeAutoId = null) {
    // If numeroTelaio is empty, no check needed
    if (!numeroTelaio || numeroTelaio.trim() === '') {
      return { isDuplicate: false, existingAuto: null }
    }

    let query = supabase
      .from(this.tableName)
      .select('id, targa, marca, modello, numero_telaio')
      .eq('numero_telaio', numeroTelaio.trim())

    // If editing an existing auto, exclude it from the check
    if (excludeAutoId) {
      query = query.neq('id', excludeAutoId)
    }

    const { data, error } = await query

    if (error) throw error

    const isDuplicate = data && data.length > 0
    const existingAuto = isDuplicate ? data[0] : null

    return {
      isDuplicate,
      existingAuto
    }
  }




}

export class ClienteService extends SupabaseService {
  constructor() {
    super('clienti')
  }

  async getWithAutoCount() {
    const { data, error } = await supabase
      .from(this.tableName)
      .select(`
        *,
        auto_count:auto(count)
      `)
      .order('nome', { ascending: true })
    
    if (error) throw error
    return data || []
  }

  async searchByNameOrEmail(searchTerm) {
    return this.search(searchTerm, ['nome', 'cognome', 'email', 'telefono'])
  }
}

export class CampataService extends SupabaseService {
  constructor() {
    super('campate')
  }

  async getWithStructure() {
    const { data, error } = await supabase
      .from(this.tableName)
      .select(`
        *,
        sezioni:sezione_id(
          nome,
          tettoie:tettoia_id(nome)
        ),
        auto:auto(targa, marca, modello)
      `)
      .order('nome', { ascending: true })
    
    if (error) throw error
    return data || []
  }

  async getAvailable() {
    return this.filter({ occupata: false })
  }

  async getOccupied() {
    return this.filter({ occupata: true })
  }
}

// Factory function to create services
export function createService(tableName) {
  switch (tableName) {
    case 'auto':
      return new AutoService()
    case 'clienti':
      return new ClienteService()
    case 'campate':
      return new CampataService()
    default:
      return new SupabaseService(tableName)
  }
}

// Export specific service instances
export const autoService = new AutoService()
export const clienteService = new ClienteService()
export const campataService = new CampataService()
export const statoService = new SupabaseService('stati')
export const tettoiaService = new SupabaseService('tettoie')
export const sezioneService = new SupabaseService('sezioni')